/**
 * @fileoverview marketing-shell strings barrel — the EN/ES bundles plus
 *   `loadMarketingShellStrings(i18nInstance, locales)`, which registers them under the
 *   `marketingShell` namespace on a react-i18next instance.
 * @package @puffin-core/marketing-shell
 *
 * ## 🔴 LEAF-ONLY by design — this subpath must not import a component
 *
 * `@puffin-core/i18n-namespaces` imports this module at APP BOOT from every web app and every
 * marketing site, so anything reachable from here lands in every boot chunk. It imports only its own
 * frozen data modules and a zero-import namespace constant.
 *
 * @module @puffin-core/marketing-shell/strings
 */

import { MARKETING_SHELL_EN } from './en.js';
import { MARKETING_SHELL_ES } from './es.js';
import { MARKETING_SHELL_NAMESPACE } from './namespace.js';

export { MARKETING_SHELL_EN, MARKETING_SHELL_ES, MARKETING_SHELL_NAMESPACE };

const BUNDLES = Object.freeze({ en: MARKETING_SHELL_EN, es: MARKETING_SHELL_ES });

/**
 * The locales this package ships bundles for — DERIVED from `BUNDLES`, never typed out (a restated
 * list is a second copy of a fact, ENUM-DRIFT-001).
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more marketing-shell bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a locale is unsupported.
 */
export function loadMarketingShellStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadMarketingShellStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadMarketingShellStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, MARKETING_SHELL_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
